import { SVGProps } from 'react';
import { FilterFunnel01 } from '@kula-ai/icons/dual-tone/FilterFunnel01';
import { MessageChatCircle } from '@kula-ai/icons/dual-tone/MessageChatCircle';
import { CurrencyDollarCircle } from '@kula-ai/icons/dual-tone/CurrencyDollarCircle';
import { UserPlus01 } from '@kula-ai/icons/dual-tone/UserPlus01';
import { Briefcase01 } from '@kula-ai/icons/dual-tone/Briefcase01';
import { Users03 } from '@kula-ai/icons/dual-tone/Users03';
import { BarLineChart } from '@kula-ai/icons/dual-tone/BarLineChart';
import { Dotpoints01 } from '@kula-ai/icons/dual-tone/Dotpoints01';
import { MinusCircle } from '@kula-ai/icons/outline/MinusCircle';
import { usePermissionGuard } from '@/hooks/usePermissionGuard';
import { useKFlags } from '@/hooks/use-k-flags';
import { ImageUserUp } from '@kula-ai/icons/dual-tone/ImageUserUp';
import { FilterFunnelSearch } from '@/assets/icons/reports';

export interface Report {
  id: string;
  name: string;
  icon: (_props: SVGProps<SVGSVGElement>) => JSX.Element;
  url: string;
  desc: string;
  color: string;
  bgColor: string;
}

export const useDefaultReports = ({ isReadOnly = false } = {}): Report[] => {
  // @ts-expect-error - isReadOnly is not used
  const { isFlowEnabled } = usePermissionGuard();
  const { feReq, feRecruiterPerformance, liveJobPipelineReports } = useKFlags();
  const atsReports = [
    ...(liveJobPipelineReports
      ? [
          {
            id: 'live_job_pipeline',
            name: 'Current job pipeline',
            url: '/reports/live_job_pipeline',
            desc: 'View current snapshot of candidates across application review, interview, offer, and hired stages',
            icon: FilterFunnelSearch,
            color: '#F5BE40',
            bgColor: '#FDF2DA',
          },
        ]
      : []),
    {
      id: 'pipelines',
      name: 'Pipeline history',
      url: '/reports/pipeline',
      desc: 'Track candidate progress across stages, gaining insight into the full funnel journey',
      icon: FilterFunnel01,
      color: '#F5BE40',
      bgColor: '#FDF2DA',
    },
    {
      id: 'interviews',
      name: 'Interviews',
      icon: MessageChatCircle,
      url: '/reports/interviews',
      desc: 'Keep track of interviews and optimize your interview loop to enhance candidate experience',
      color: '#B013FF',
      bgColor: '#EFD7FB',
    },
    {
      id: 'offers',
      name: 'Offers',
      icon: CurrencyDollarCircle,
      url: '/reports/offers',
      desc: 'Optimize your offer management and streamline hiring decisions to boost hiring success',
      color: '#EC8030',
      bgColor: '#FBEAD6',
    },
    {
      id: 'hires',
      name: 'Hires',
      icon: UserPlus01,
      url: '/reports/hires',
      desc: 'Track your hiring process seamlessly and make data-driven decisions',
      color: '#5CC9FA',
      bgColor: '#DEF4FE',
    },
    {
      id: 'rejections',
      name: 'Rejections',
      icon: MinusCircle,
      url: '/reports/rejections',
      desc: 'Track rejections across your organization to identify patterns and improve hiring throughput',
      color: '#EA3369',
      bgColor: '#FBD6E1',
    },
    {
      id: 'contacts',
      name: 'Contacts',
      icon: Users03,
      url: '/reports/contacts',
      desc: 'Understand candidate trends and enhance your hiring strategy to attract top talent',
      color: '#6762FF',
      bgColor: '#DFDEFB',
    },
    {
      id: 'jobs',
      name: 'Jobs',
      icon: Briefcase01,
      url: '/reports/jobs',
      desc: 'Streamline your job tracking process and boost hiring efficiency',
      color: '#2FC38E',
      bgColor: '#DEFBEE',
    },
    {
      id: 'eeo',
      name: 'EEO',
      icon: Dotpoints01,
      url: '/reports/eeo',
      desc: 'Monitor your diversity and EEO compliance to ensure a fair and equitable hiring process',
      color: '#00919C',
      bgColor: '#DEF9FB',
    },
  ].filter(Boolean);

  const flowReports = [
    {
      id: 'flowhealth',
      name: 'Flow Health',
      icon: BarLineChart,
      url: '/reports/flow-health',
      desc: 'Track top of funnel sourcing activity and its impact on your candidate pipeline',
      color: '#F5BE40',
      bgColor: '#FDF2DA',
    },
  ];

  const reports = atsReports;

  if (isFlowEnabled || isReadOnly) {
    reports.push(...flowReports);
  }

  if (feRecruiterPerformance) {
    reports.push({
      id: 'recruiter-performance',
      name: 'Recruiter performance',
      icon: ImageUserUp,
      url: '/reports/recruiter-performance',
      desc: 'Track candidate progress across stages, gaining insight into the full funnel journey',
      color: '#B013FF',
      bgColor: '#EFD7FB',
    });
  }

  if (feReq) {
    reports.push({
      id: 'requisitions',
      name: 'Requisitions',
      icon: Briefcase01,
      url: '/reports/requisitions',
      desc: 'Track requisitions and optimize your hiring process to boost hiring success',
      color: '#00919C',
      bgColor: '#DEF9FB',
    });
  }

  return reports;
};
